import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import { OrderEditorGuidelineType } from '@/store/types'


/**
 * Guideline Warning
 * Used internally when inspecting shapes
 */
export default class GuidelineWarning {
  constructor(warningData = {}) {
    
    this.id = uuidv4()
    this.type = warningData.type
    this.description = warningData.description
    this.glassShapeId = warningData.glassShapeId
    this.centerCutoutId = warningData.centerCutoutId
    this.segmentId = warningData.segmentId || 0
    this.position = new THREE.Vector2(0, 0)
    this.displayPoint = new THREE.Vector2(0, 0)
    this.clearanceRequired = warningData.clearanceRequired || 0
    this.clearance = warningData.clearance || 0
    this.radiusRequired = warningData.radiusRequired || 0
    this.radius = warningData.radius || 0
    this.radiusArcPercent = warningData.radiusArcPercent || 0
    this.radiusArcTestPercent = warningData.radiusArcTestPercent || 0
    this.shapeThickness = warningData.shapeThickness
    this.splitCount = warningData.splitCount || 0
    this.splitLimit = warningData.splitLimit || 0
    this.fixable = !!warningData.fixable

    if(warningData.position)
      this.position.set(warningData.position.x, warningData.position.y)
    if(warningData.displayPoint)
      this.displayPoint.set(warningData.displayPoint.x, warningData.displayPoint.y)
  }


  /**
   * Get the icon of the warning for display
   */
  getIcon() {
    switch(this.type) {
      case OrderEditorGuidelineType.centerCutoutEdgeToEdge:
        return 'far fa-border-outer'
      case OrderEditorGuidelineType.centerCutoutEdgeToPanelEdge:
        return `far fa-border-top`
      case OrderEditorGuidelineType.centerCutoutEdgeToPanelCorner:
        return `far fa-border-style`
      case OrderEditorGuidelineType.centerCutoutRadius:
        return `far fa-circle`
      case OrderEditorGuidelineType.panelCornerRadius:
        return `far fa-draw-circle`
      case OrderEditorGuidelineType.splitLimit:
        return `far fa-border-all`
    }
  }


  /**
   * Get the name of the warning
   */
  getName() {
    switch(this.type) {
      case OrderEditorGuidelineType.centerCutoutEdgeToEdge:
        return 'Cutout Edge to Cutout Edge Warning'
      case OrderEditorGuidelineType.centerCutoutEdgeToPanelEdge:
        return `Cutout Edge to Panel Edge Warning`
      case OrderEditorGuidelineType.centerCutoutEdgeToPanelCorner:
        return `Cutout Edge to Panel Corner Warning`
      case OrderEditorGuidelineType.centerCutoutRadius:
        return `Center Cutout Radius Warning`
      case OrderEditorGuidelineType.panelCornerRadius:
        return `Corner Radius Warning`
      case OrderEditorGuidelineType.splitLimit:
        return `Split Limit Warning`
    }
  }


  /**
   * Generate the HTML description for the guideline warning
   */
  toHTMLString() {
    var description = 'Unknown warning'

    let units = 'mm'
    let clearanceRequired = this.clearanceRequired.toFixed(0)
    let clearance = this.clearance.toFixed(0)
    let radiusRequired = this.radiusRequired.toFixed(0)
    let radius = this.radius.toFixed(1)
    let radiusTestPercent = this.radiusArcTestPercent.toFixed(0)
    let arcTestAbove = this.radiusArcPercent >= this.radiusArcTestPercent

    let thickness = this.shapeThickness.toFixed(2)

    switch(this.type) {
      case OrderEditorGuidelineType.centerCutoutEdgeToEdge:
        description = `Center cutouts must have a minimum <strong>${clearanceRequired}${units}</strong> clearance between cutout edges for <strong>${thickness}${units}</strong> thickness.<br />`
        description += `Distance is currently <strong>${clearance}mm</strong>`
        break
      case OrderEditorGuidelineType.centerCutoutEdgeToPanelEdge:
        description = `Center cutouts must have a minimum <strong>${clearanceRequired}${units}</strong> clearance between cutout edge and panel edges for <strong>${thickness}${units}</strong> thickness.<br />`
        description += `Distance is currently <strong>${clearance}mm</strong>`
        break
      case OrderEditorGuidelineType.centerCutoutEdgeToPanelCorner:
        description = `Center cutouts must have a minimum <strong>${clearanceRequired}${units}</strong> clearance between cutout edge and panel corners for <strong>${thickness}${units}</strong> thickness.<br />`
        description += `Distance is currently <strong>${clearance}mm</strong>`
        break

      case OrderEditorGuidelineType.centerCutoutRadius:
        description = `Center cutout minimum corner radius is <strong>${radiusRequired}${units}</strong> where corner coverage is ${arcTestAbove ? 'above' : 'below'} <strong>${radiusTestPercent}%</strong> for <strong>${thickness}${units}</strong> thickness.<br />`
        description += `Corner radius is currently <strong>${radius}mm</strong>`
        break

      case OrderEditorGuidelineType.panelCornerRadius:
        description = `Panel cutout minimum corner radius is <strong>${radiusRequired}${units}</strong> where corner coverage is ${arcTestAbove ? 'above' : 'below'} <strong>${radiusTestPercent}%</strong> for <strong>${thickness}${units}</strong> thickness.<br />`
        description += `Corner radius is currently <strong>${radius}mm</strong>`
        break
      
      case OrderEditorGuidelineType.splitLimit:
        description = `Maximum number of splits available is <strong>${this.splitLimit}</strong>.<br />`
        description += `Number of splits is <strong>${this.splitCount}</strong>`
        break
    }

    return description
  }
}
